import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ActionCreateRequestPayload,
  ActionUpdateRequestPayload,
  SearchActionPayload,
} from "@/domain/action.interface";
import { ActionsService } from "@/services/actions.service";

export const useActions = (
  {
    sortField,
    sortOrder,
    pageNumber,
    pageSize,
    search,
    status,
  }: SearchActionPayload,
  enabled = true,
) => {
  const {
    data: actionsResponse,
    isLoading: isLoadingActions,
    refetch: refetchActions,
  } = useQuery({
    queryKey: [
      "coyn-actions",
      sortField,
      sortOrder,
      pageNumber,
      pageSize,
      search,
      status,
    ],
    queryFn: () =>
      ActionsService.search({
        sortField,
        sortOrder,
        pageNumber,
        pageSize,
        search,
        status,
      }),
    enabled,
  });

  return {
    actions: actionsResponse?.actions,
    totalCount: actionsResponse?.totalCount,
    isLoadingActions,
    refetchActions,
  };
};

export const useActionCreateOne = () => {
  return useMutation({
    mutationFn: (data: ActionCreateRequestPayload) =>
      ActionsService.create(data),
  });
};

export const useActionUpdateOne = () => {
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: ActionUpdateRequestPayload;
    }) => {
      await ActionsService.update({ id, data });
    },
  });
};

export const useActionDeleteOne = () => {
  return useMutation({
    mutationFn: (id: string) => ActionsService.softDelete(id),
  });
};

export const useAllActions = (enabled = true) => {
  const {
    data: allActions,
    isLoading: isLoadingAllActions,
    refetch: refetchAllActions,
  } = useQuery({
    queryKey: ["coyn-all-actions"],
    queryFn: () => ActionsService.findAll(),
    enabled,
  });

  return {
    allActions,
    isLoadingAllActions,
    refetchAllActions,
  };
};
